import React, { useContext, useRef } from "react";
import { captureMessage } from "@sentry/browser";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import { buildLink, getPath } from "common/routing";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import {
  MapsForAccountQuery,
  useGetOrCreateGuestPropertyLazyQuery,
} from "../../generated/graphql";
import useMapIcons from "../../hooks/useMapIcons";
import { setTitle } from "../../utils/title";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Button } from "../Common/Button";
import { Container } from "../Maps/__styles__/FullPageMap";
import FullPageMap, { FullPageMapContainerProps } from "../Maps/FullPageMap";
import { MapRef, Point } from "../Maps/LayeredMap";
import { ClickObjectMenuLayerConfig } from "../Maps/LayeredMap/clickObjectMenuLayer";
import { CustomMapLayerConfig } from "../Maps/LayeredMap/customMapLayer";
import { MarkerLayerConfig } from "../Maps/LayeredMap/markerLayer";
import { ParcelLayerConfig } from "../Maps/LayeredMap/parcelLayer";
import { PropertyLayerConfig } from "../Maps/LayeredMap/propertyLayer";
import { SavedViewsLayerConfig } from "../Maps/LayeredMap/savedViewsLayer";
import { LayerContext } from "../Maps/layers";
import { isCustomMap, isProperty } from "../Maps/Map";
import { Exit } from "./__styles__/Explore";
import {
  accountTitle,
  HELPSCOUT_BEACON_TYPES,
  useHelpscoutBeacon,
} from "./utils";

const ExitButton = ({ handleExitClick }: { handleExitClick: () => void }) => (
  <Exit>
    <Button
      onClick={handleExitClick}
      styleVariant="outlineLight"
      leftIconName="x"
      style={{
        width: "30px",
        height: "30px",
        minWidth: "unset",
        padding: "0px 5px 0px 5px",
      }}
    />
  </Exit>
);

const useLatLong = () => {
  const location = useLocation();
  const params = queryString.parse(location.search);
  const longitude = parseFloat(params.lng as string);
  const latitude = parseFloat(params.lat as string);

  return {
    latitude,
    longitude,
  };
};

export const GuestMap = ({
  account,
}: {
  account: NonNullable<MapsForAccountQuery["account"]>;
}) => {
  const mapRef = useRef<Maybe<MapRef>>(null);
  useMapIcons(mapRef);

  const history = useHistory<{ prevLocation: string }>();
  const location = useLocation();
  const { t } = useTranslation();

  const { visibleSavedViews: getVisibleSavedViews } = useContext(LayerContext);
  const { account: authAccount } = useContext(AuthContext);
  const customMaps = authAccount?.customMaps ?? [];

  const { latitude, longitude } = useLatLong();

  const [getOrCreateProperty] = useGetOrCreateGuestPropertyLazyQuery({
    onCompleted: propertyData => {
      if (propertyData.getProperty) {
        navigateToPPP(propertyData.getProperty.id, {
          latitude: propertyData.getProperty.latitude!,
          longitude: propertyData.getProperty.longitude!,
        });
      } else {
        history.push({
          pathname: buildLink("noProperty"),
        });
      }
    },
  });

  useHelpscoutBeacon(HELPSCOUT_BEACON_TYPES.OVERVIEW);

  const prevLocation =
    history.location.state?.prevLocation || getPath("guestHome");

  const handleExitClick = () => {
    history.push(prevLocation);
  };

  const navigateToPPP = (propertyId: string, point: Point) => {
    const params = queryString.parse(location.search);
    const firmId = params.firmId as string;

    const prevLocation = buildLink(
      "explore",
      {},
      {
        lat: point.latitude.toString(),
        lng: point.longitude.toString(),
        firmId,
      }
    );

    history.replace(prevLocation);

    history.push({
      pathname: buildLink("residentProperty", {
        propertyId: propertyId,
      }),
      state: {
        prevLocation,
      },
    });
  };

  const onParcelClick: ParcelLayerConfig["onClick"] = parcels => {
    // we should have exactly one parcel
    // because of the disambiguation menu layer
    if (!arrayHasAtLeastOneItem(parcels)) {
      captureMessage("Guest map clicked on zero parcels");
      return;
    } else if (parcels.length > 1) {
      captureMessage("Guest map clicked on multiple parcels", {
        extra: {
          parcels,
        },
      });
    }
    const parcel = parcels[0];

    track("Property clicked", { guest: true });

    // If parcel has one property, regardless of address, go to PPP
    if (parcel.propertyIds?.length === 1) {
      navigateToPPP(parcel.propertyIds[0]!, parcel.point);
      return;
    }

    // If parcel has address, find (or create), then to PPP
    if (parcel.address) {
      void getOrCreateProperty({
        variables: {
          parcelId: parcel.id,
          address: parcel.fullAddress,
          longitude: parcel.point.longitude,
          latitude: parcel.point.latitude,
        },
      });
      return;
    }

    // If parcel has no address AND many properties, pick first and go to PPP
    if (parcel.propertyIds?.length) {
      navigateToPPP(parcel.propertyIds[0]!, parcel.point);
      return;
    }

    // If parcel has no address AND no properties, (find or) create, then to PPP
    void getOrCreateProperty({
      variables: {
        parcelId: parcel.id,
        longitude: parcel.point.longitude,
        latitude: parcel.point.latitude,
      },
    });
  };

  const onSearchResult: FullPageMapContainerProps["onSearchResult"] = ({
    point,
    address,
    propertyId,
    geocacheParcelId,
  }) => {
    track("Property clicked", { guest: true, search: true });
    void getOrCreateProperty({
      variables: {
        address,
        latitude: point.latitude,
        longitude: point.longitude,
        id: propertyId,
        parcelId: geocacheParcelId,
      },
    });
  };

  const onSavedViewClick: SavedViewsLayerConfig["onClick"] = object => {
    if (isProperty(object)) {
      navigateToPPP(object.id, object.point);
    } else if (isCustomMap(object)) {
      // TODO: navigate to OIP once implemented for guests
    }
  };
  const onPropertyClick: PropertyLayerConfig["onClick"] = property => {
    navigateToPPP(property.id, property.point);
  };

  setTitle(`${t("tab-explore")} | ${accountTitle()}`);

  const point = latitude && longitude ? { latitude, longitude } : null;
  const markerConfig = { value: point } satisfies MarkerLayerConfig;
  const parcelConfig = {
    interactive: { hover: true, click: true },
    onClick: onParcelClick,
    value: { point },
  } satisfies ParcelLayerConfig;
  const propertyConfig = {
    filterHidden: true,
    interactive: {
      hover: true,
      click: true,
    },
    onClick: onPropertyClick,
  } satisfies PropertyLayerConfig;

  const customMapsConfig = {
    interactive: { hover: false },
    maps: customMaps,
  } satisfies CustomMapLayerConfig;

  const visibleSavedViews = getVisibleSavedViews();

  const savedViewsConfig = {
    onClick: onSavedViewClick,
    value: visibleSavedViews,
    interactive: {
      hover: !!visibleSavedViews?.length,
      click: !!visibleSavedViews?.length,
    },
    savedViews: account.savedViews,
  } satisfies SavedViewsLayerConfig;

  const clickObjectMenuConfig = {
    interactive: { click: true, hover: true },
    maps: [],
    savedViews: [],
  } satisfies ClickObjectMenuLayerConfig;

  return (
    <Container>
      <FullPageMap
        account={account}
        markerConfig={markerConfig}
        parcelConfig={parcelConfig}
        propertyConfig={propertyConfig}
        savedViewsConfig={savedViewsConfig}
        customMapsConfig={customMapsConfig}
        clickObjectMenuConfig={clickObjectMenuConfig}
        ref={mapRef}
        onSearchResult={onSearchResult}
      >
        <ExitButton handleExitClick={handleExitClick} />
      </FullPageMap>
    </Container>
  );
};
